"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import RemovedSponsorshipNotice from "@/components/RemovedSponsorshipNotice";
import { AnimatePresence, m } from "framer-motion";

import { useSponsorshipChildren } from "@/hooks/useSponsorshipChildren";
import useFocusTrap from "@/hooks/useFocusTrap";
import { useCart, useCartActions } from "@/tools/medusa";
import type { CartItem as CartItemType, RecurringFrequency } from "@/tools/medusa/types";
import { isDonationItem } from "@/tools/medusa/helpers";
import type { ChildDetails } from "@/types/sponsorship";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import Text from "@/components/Text";
import CartItem from "@/components/Cart/CartItem";
import CartSubtotal from "@/components/Cart/CartSubtotal";
import SponsorshipHoldTimer from "@/components/Cart/SponsorshipHoldTimer";
import Logomark from "@/assets/logo/logomark.svg";
import classNames from "@/helpers/classNames";
import { ANALYTICS_EVENT, useAnalytics } from "@/tools/analytics";
import styles from "./styles.module.scss";

export interface CartDrawerProps {
  className?: string;
}

interface CartDrawerSectionProps {
  title: string;
  items: CartItemType[];
  /**
   * Show how long the children in this section stay reserved. The timer
   * hides itself when nothing here is tied to a child, so this is safe on
   * the mixed donations section.
   */
  showHoldTimer?: boolean;
  sponsorshipChildren?: Record<string, ChildDetails | null>;
  onUpdateQuantity: (lineItemId: string, quantity: number) => Promise<void>;
  onRemove: (lineItemId: string) => Promise<void>;
  onUpdateFrequency?: (lineItemId: string, frequency: RecurringFrequency) => void;
}

const CartDrawerSection = ({
  title,
  items,
  showHoldTimer = false,
  sponsorshipChildren,
  onUpdateQuantity,
  onRemove,
  onUpdateFrequency,
}: CartDrawerSectionProps) => (
  <section className={styles.section} data-theme="light">
    <header className={styles.sectionHeader}>
      <div className={styles.title}>
        <Logomark className={styles.sectionIcon} width={18} height={18} aria-hidden="true" />
        <Text
          text={title}
          variant="label"
          size="lg"
          weight="semibold"
          className={styles.sectionLabel}
        />
      </div>
      {showHoldTimer && (
        <SponsorshipHoldTimer
          items={items}
          sponsorshipChildren={sponsorshipChildren}
          className={styles.holdTimers}
        />
      )}
    </header>
    <div className={styles.sectionItems}>
      {items.map((item) => (
        <CartItem
          key={item.id}
          item={item}
          child={sponsorshipChildren?.[item.metadata?.child_id ?? ""] ?? undefined}
          onUpdateQuantity={onUpdateQuantity}
          onRemove={onRemove}
          onUpdateFrequency={onUpdateFrequency}
        />
      ))}
    </div>
  </section>
);

const CartDrawer = ({ className }: CartDrawerProps) => {
  const router = useRouter();
  const {
    items,
    itemCount,
    subtotal,
    shippingTotal,
    taxTotal,
    total,
    isTaxInclusive,
    isLoading,
    isCartOpen,
    error,
  } = useCart();
  const { updateItem, updateItemMetadata, removeItem, closeCart, clearError } = useCartActions();

  /**
   * `view_cart` for the drawer. It previously fired only on the cart *page*, so
   * a supporter who added something, reviewed the drawer and checked out from
   * it never registered as having viewed their cart at all.
   *
   * Keyed on the open state and contents rather than hooked to `openCart`,
   * because three donation forms call `openCart` programmatically right after
   * adding — that measures us opening the drawer, not the supporter looking at
   * it. `useAnalytics` dedupes on the key, so re-renders while it sits open
   * don't re-fire.
   */
  useAnalytics({
    event: ANALYTICS_EVENT.VIEW_CART,
    payload: { items, subtotal },
    key: `drawer::${itemCount}::${subtotal}`,
    enabled: isCartOpen && items.length > 0,
  });

  // Split items by product type so the drawer can render the two Figma
  // groups: "Sponsorships & Donations" (child sponsorships, recurring giving
  // and one-off donations) and "Better World Gifts" (physical/digital
  // products and anything not identifiable as a donation). Rendered in that
  // order — see the note at the render site.
  const { gifts, donations } = useMemo(() => {
    const giftItems: CartItemType[] = [];
    const donationItems: CartItemType[] = [];
    for (const item of items) {
      if (isDonationItem(item)) {
        donationItems.push(item);
      } else {
        giftItems.push(item);
      }
    }
    return { gifts: giftItems, donations: donationItems };
  }, [items]);

  // Fetch sponsored-child details for sponsorship lines in the donations
  // bucket so the drawer renders child name + country/age + masked photo
  // instead of generic product fields.
  const { data: sponsorshipChildren } = useSponsorshipChildren(donations);

  const handleCheckout = useCallback(() => {
    closeCart();
    router.push("/checkout");
  }, [closeCart, router]);

  // Persist the supporter's frequency choice via the cart store's
  // metadata-patch action — matches CartPageClient so the slideout and
  // full cart write the same field through the same path.
  const handleUpdateFrequency = useCallback(
    async (lineItemId: string, frequency: RecurringFrequency) => {
      await updateItemMetadata(lineItemId, { recurring_frequency: frequency });
    },
    [updateItemMetadata],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeCart();
      }
    };

    if (isCartOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isCartOpen, closeCart]);

  const panelRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(panelRef, isCartOpen);

  // Focus follows the drawer: into it when it opens, and back to whatever
  // opened it when it closes. The trap above only catches Tab, so without this
  // focus stayed on the page behind, which `aria-modal` tells a screen reader
  // to ignore, and was lost altogether when the drawer closed.
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!isCartOpen) {
      return;
    }
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus({ preventScroll: true });
    return () => {
      if (opener?.isConnected) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [isCartOpen]);

  const drawerContent = (
    <AnimatePresence mode="wait">
      {isCartOpen && (
        // Portalled outside any themed subtree, so `--fg-brand`, `--fg-muted`
        // and `--stroke-subtle` resolved to nothing — silently invalidating the
        // focus ring, the muted text colour and the row dividers. The panel is
        // `--flag-white`, matching this theme.
        <div data-theme="white" className={classNames(styles.drawer, className)}>
          {/* Overlay */}
          <m.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeCart}
          />

          {/* Panel */}
          <m.div
            className={styles.panel}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Shopping cart"
          >
            {/* Header */}
            <div className={styles.header}>
              <Text
                className={styles.heading}
                text={`Cart${itemCount > 0 ? ` (${itemCount})` : ""}`}
                variant="heading"
                size="sm"
              />
              <button
                ref={closeButtonRef}
                type="button"
                className={styles.closeButton}
                onClick={closeCart}
                aria-label="Close cart"
                // Where focus goes when Remove empties the cart (useFocusAfterRemove).
                data-cart-focus-fallback=""
              >
                <Icon title="close" size="sm" color="currentColor" />
              </button>
            </div>

            {/* Content */}
            <div className={styles.content}>
              {error && (
                <div className={styles.error} role="alert" aria-live="polite">
                  <Text text={error} size="sm" />
                  <Button
                    onClick={clearError}
                    ariaLabel="Dismiss error"
                    icon="close"
                    variant="content"
                    size="none"
                    iconHover={false}
                  />
                </div>
              )}
              {items.length === 0 ? (
                <div className={styles.empty}>
                  <Icon title="shoppingBagOpen" size="lg" color="currentColor" />
                  <Text
                    className={styles.emptyText}
                    text="Your cart is empty"
                    color="themeFgMuted"
                  />
                </div>
              ) : (
                <div className={styles.sections}>
                  {/*
                    Sponsorships first, deliberately. This group is the only one
                    that can carry a hold timer, and a child reservation expires
                    — putting it under the gifts meant the countdown sat below
                    the fold on a cart with a few products in it, which is the
                    one thing in here a supporter cannot afford to miss. Also
                    matches the cart page, which already leads with "Your
                    Sponsorships".
                  */}
                  {donations.length > 0 && (
                    <CartDrawerSection
                      title="Sponsorships & Donations"
                      items={donations}
                      showHoldTimer
                      sponsorshipChildren={sponsorshipChildren}
                      onUpdateQuantity={updateItem}
                      onRemove={removeItem}
                      onUpdateFrequency={handleUpdateFrequency}
                    />
                  )}
                  {gifts.length > 0 && (
                    <CartDrawerSection
                      title="Better World Gifts"
                      items={gifts}
                      onUpdateQuantity={updateItem}
                      onRemove={removeItem}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className={styles.footer}>
                <CartSubtotal
                  subtotal={subtotal}
                  shipping={shippingTotal}
                  taxes={taxTotal}
                  total={total}
                  isTaxInclusive={isTaxInclusive}
                  onCheckout={handleCheckout}
                  isLoading={isLoading || items.length === 0}
                />
              </div>
            )}
          </m.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (typeof window === "undefined") {
    return null;
  }

  return createPortal(
    <>
      {drawerContent}
      {/* Explains any child taken out of the cart since they last looked.
          Sits outside `drawerContent` so the news still lands if the drawer
          is closed again before they read it. */}
      {isCartOpen && <RemovedSponsorshipNotice />}
    </>,
    document.body,
  );
};

export default CartDrawer;
